import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Leaf, Zap, Award, Eye } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ProductCard from '../components/ProductCard';

import { products } from '../data/products';

gsap.registerPlugin(ScrollTrigger);

const Home: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const productsRef = useRef<HTMLDivElement>(null);
  const deliveryRef = useRef<HTMLDivElement>(null);
  const advantagesScrollRef = useRef<HTMLDivElement>(null);
  const [activeAdvantage, setActiveAdvantage] = useState(0.0);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero анимация с will-change оптимизацией
      if (titleRef.current) {
        gsap.set(titleRef.current, { willChange: 'transform, opacity' });
        gsap.fromTo(titleRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            onComplete: () => { 
              if (titleRef.current) gsap.set(titleRef.current, { willChange: 'auto' }); 
            }
          }
        );
      }

      if (subtitleRef.current) {
        gsap.set(subtitleRef.current, { willChange: 'transform, opacity' });
        gsap.fromTo(subtitleRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            delay: 0.3,
            onComplete: () => { 
              if (subtitleRef.current) gsap.set(subtitleRef.current, { willChange: 'auto' }); 
            }
          }
        );
      }

      if (buttonRef.current) {
        gsap.set(buttonRef.current, { willChange: 'transform, opacity' });
        gsap.fromTo(buttonRef.current,
          { 
            opacity: 0, 
            y: 30,
            scale: 0.9,
            rotation: -2
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotation: 0,
            duration: 1.2,
            ease: "back.out(1.7)",
            delay: 0.6,
            onComplete: () => { 
              if (buttonRef.current) gsap.set(buttonRef.current, { willChange: 'auto' }); 
            }
          }
        );
      }

      // Анимация advantages
      if (advantagesRef.current) {
        const advantageElements = advantagesRef.current.children;
        gsap.set(advantageElements, { willChange: 'transform, opacity' });
        gsap.fromTo(advantageElements,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out",
            delay: 1.2,
            onComplete: () => { 
              gsap.set(advantageElements, { willChange: 'auto' }); 
            }
          }
        );
      }

      // Анимации при скролле с оптимизацией
      const sections = [
        { ref: aboutRef, trigger: '.about-section' },
        { ref: productsRef, trigger: '.products-section' },
        { ref: deliveryRef, trigger: '.delivery-section' }
      ];

      sections.forEach(({ ref, trigger }) => {
        if (ref.current) {
          const elements = ref.current.children;
          gsap.fromTo(elements,
            { opacity: 0, y: 50 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.2,
              ease: 'power2.out',
              scrollTrigger: {
                trigger,
                start: 'top 80%',
                end: 'bottom 5%',
                toggleActions: 'play none none reverse',
                scrub: false,
                fastScrollEnd: true,
                onToggle: (self) => {
                  // Управляем will-change для производительности
                  if (self.isActive) {
                    gsap.set(elements, { willChange: 'transform, opacity' });
                  } else {
                    gsap.set(elements, { willChange: 'auto' });
                  }
                }
              }
            }
          );
        }
      });
    });

    // Cleanup функция для очистки всех GSAP анимаций
    return () => {
      ctx.revert();
      ScrollTrigger.refresh();
    };
  }, []);

  const advantages = [
    {
      icon: CheckCircle,
      title: 'Качество',
      description: 'Вся продукция проходит строгий контроль качества и соответствует международным стандартам'
    },
    {
      icon: Leaf,
      title: 'Экология',
      description: 'Используем только натуральные компоненты, безопасные для окружающей среды'
    },
    {
      icon: Zap,
      title: 'Инновации',
      description: 'Применяем современные технологии для создания эффективных продуктов'
    },
    {
      icon: Award,
      title: 'Опыт',
      description: 'Команда специалистов сочетает знания и многолетнюю практику'
    }
  ];

  const advantagesRef = useRef<HTMLDivElement>(null);

  const featuredProducts = [products[0], products[1], products[2], products[4]];

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  // Throttled обработчик скролла для преимуществ
  const handleAdvantagesScroll = useCallback(() => {
    if (!advantagesScrollRef.current) return;
    
    const scrollLeft = advantagesScrollRef.current.scrollLeft;
    const itemWidth = 256; // w-64 = 16rem = 256px
    const maxScroll = itemWidth * (advantages.length - 1);
    const scrollProgress = Math.min(scrollLeft / maxScroll, 1);
    
    // Используем requestAnimationFrame для оптимизации
    requestAnimationFrame(() => {
      setActiveAdvantage(scrollProgress);
    });
  }, [advantages.length]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 ref={titleRef} className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Здоровое будущее начинается здесь
          </h1>
                      <p ref={subtitleRef} className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              <span className="block md:hidden">Натуральные продукты для жизни и природы</span>
              <span className="hidden md:block">
                Мы предлагаем натуральные удобрения, корма и добавки для животных, а также качественные 
                биологически активные добавки (БАДы) для поддержания вашего здоровья. Вся продукция 
                выпускается проверенным производителем, проходит строгий контроль качества и отличается 
                безопасным, экологичным составом.
              </span>
            </p>
          <Link ref={buttonRef} to="/shop" className="btn-primary inline-flex items-center space-x-2">
            <span>Перейти в магазин</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section id="about" ref={aboutRef} className="about-section section-padding bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              О компании
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              ПРОТЭКО — компания, специализирующаяся на производстве и поставке экологически чистых решений. 
              Мы заботимся о здоровье людей и окружающей среды, предлагая только качественные и безопасные товары.
            </p>
          </div>

          {/* Преимущества с горизонтальным скроллом для мобильных */}
          <div ref={advantagesRef} className="md:hidden">
            <div 
              ref={advantagesScrollRef}
              onScroll={handleAdvantagesScroll}
              className="flex overflow-x-auto space-x-6 pb-4 scrollbar-hide"
            >
              {advantages.map((advantage, index) => (
                <div key={index} className="text-center group flex-shrink-0 w-64">
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center group-hover:bg-primary-200 dark:group-hover:bg-primary-800 transition-colors duration-200">
                    <advantage.icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {advantage.description}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Индикатор скролла - движущаяся линия */}
            <div className="flex justify-center mt-6">
              <div className="w-64 h-2 bg-gray-200 dark:bg-gray-700 rounded-full relative px-2">
                <div 
                  className="absolute top-0 h-full w-16 bg-primary-600 dark:bg-primary-400 rounded-full transition-transform duration-75 ease-out"
                  style={{
                    transform: `translateX(${activeAdvantage * 184}px)`
                  }}
                />
              </div>
            </div>
          </div>

          {/* Преимущества для десктопа */}
          <div ref={advantagesRef} className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center group-hover:bg-primary-200 dark:group-hover:bg-primary-800 transition-colors duration-200">
                  <advantage.icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {advantage.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {advantage.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section ref={productsRef} className="products-section section-padding bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Наша продукция
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Широкий ассортимент для различных нужд
            </p>
          </div>

          {/* Продукция для мобильных - 2 колонки */}
          <div className="md:hidden grid grid-cols-2 gap-4 mb-12">
            {featuredProducts.map((product, index) => (
              <div key={product.id} className="bg-white dark:bg-gray-900 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group overflow-hidden">
                {/* Изображение */}
                <div className="relative overflow-hidden">
                  <Link to={`/product/${product.id}`}>
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                    />
                  </Link>
                  {!product.inStock && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      Под заказ
                    </div>
                  )}
                </div>

                {/* Контент */}
                <div className="p-4">
                  <Link to={`/product/${product.id}`}>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 h-10 flex items-center group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200 cursor-pointer">
                      {product.name}
                    </h3>
                  </Link>
                  
                  {/* Цена с боксом глаза справа */}
                  {product.price && (
                    <div className="flex items-center justify-between">
                      <span className="text-base font-bold text-primary-600 dark:text-primary-400">
                        {formatPrice(product.price)} ₽
                      </span>
                      
                      {/* Бокс с иконкой глаза для мобильных */}
                      <Link
                        to={`/product/${product.id}`}
                        className="p-2 bg-primary-100 dark:bg-primary-800 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors duration-200"
                      >
                        <Eye className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Продукция для десктопа - 4 колонки */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {featuredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>

          <div className="text-center">
            <Link to="/products" className="btn-secondary dark:!bg-transparent dark:hover:!bg-gray-800 dark:!text-primary-400 dark:!border-primary-400 inline-flex items-center space-x-2 group">
              <span>Посмотреть все товары</span>
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Delivery Section */}
      <section id="delivery" ref={deliveryRef} className="delivery-section section-padding bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Доставка и оплата
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Удобные способы доставки и безопасные методы оплаты для вашего комфорта
            </p>
          </div>

          {/* Доставка для мобильных - 2 колонки сверху, 1 снизу */}
          <div className="md:hidden grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Быстрая доставка
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Доставляем по всей России курьерскими службами и почтой
              </p>
            </div>

            <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Безопасная оплата
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Принимаем карты, электронные платежи и наличные при получении
              </p>
            </div>
          </div>

          <div className="md:hidden text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Гарантия качества
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Все товары сертифицированы и имеют гарантию качества
            </p>
          </div>

          {/* Доставка для десктопа - 3 колонки в ряд */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Быстрая доставка
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Доставляем по всей России курьерскими службами и почтой
              </p>
            </div>

            <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Безопасная оплата
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Принимаем карты, электронные платежи и наличные при получении
              </p>
            </div>

            <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Гарантия качества
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Все товары сертифицированы и имеют гарантию качества
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
